import React, { useState } from "react";
import styled from "styled-components";
import { Body1, Body2 } from "melodies-source/Text";
import { Button } from "melodies-source/Button";
import { useArtistContext } from "Components";
import { DocumentData, DocumentReference } from "firebase/firestore";
import { useDesktopMediaQuery } from "hooks";
import { EditFieldsModal } from "./EditFieldsModal";

const fieldKeys = [
  "brandPartnerships",
  "groupType",
  "maxArtistId",
  "partnerOptIns",
  "postShowEmail",
  "sortOrder",
  "spotifyArtistId",
] as const;

export type FieldKey = (typeof fieldKeys)[number];

interface Option {
  value: string;
  label: string;
}

interface OptionWithRef extends Option {
  ref: DocumentReference<DocumentData>;
}

interface BaseField<T> {
  ref: DocumentReference<DocumentData>;
  label: string;
  placeholder?: string;
  key: FieldKey;
  value: T;
  disabled?: boolean;
}

interface TextInputField extends BaseField<string> {
  component: "text-input";
  type?: HTMLInputElement["type"];
}

interface SelectField extends BaseField<string> {
  component: "select";
  options: Option[];
}

interface SwitchField extends BaseField<boolean> {
  component: "launch";
  text?: React.ReactNode;
}

type PartnerOptInsKeys = "umg" | "wmg";
export type PartnerOptInsValue = Record<PartnerOptInsKeys, boolean>;

interface PartnerOptInsField
  extends Omit<BaseField<PartnerOptInsValue>, "ref"> {
  component: "partner-opt-ins";
  options: OptionWithRef[];
}

export type Field =
  | TextInputField
  | SelectField
  | PartnerOptInsField
  | SwitchField;

interface Props {
  title: string;
  fields: Field[];
}

export const Fields: React.FC<Props> = ({ title, fields }) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const { isAdmin } = useArtistContext();

  const isDesktop = useDesktopMediaQuery();

  const toggleModal = () => setModalIsOpen((open) => !open);

  const displayField = (field: Field) => {
    if (
      field.component === "partner-opt-ins" &&
      !Object.values(field.value).includes(true)
    )
      return null;

    let renderedValue;

    switch (field.component) {
      case "select":
        renderedValue = (
          <Body2>
            {field.options.find((opt) => opt.value === field.value)?.label ||
              field.value}
          </Body2>
        );
        break;
      case "partner-opt-ins":
        renderedValue = Object.entries(field.value)
          .filter(([_, v]) => v)
          .map(([k]) => (
            <Body2 key={k}>
              {field.options.find((opt) => opt.value === k)?.label}
            </Body2>
          ));
        break;
      default:
        renderedValue = (
          <Body2>
            {typeof field.value === "boolean"
              ? field.value
                ? "Enabled"
                : "Disabled"
              : field.value || `(click "Edit" to update field)`}
          </Body2>
        );
        break;
    }

    return (
      <FieldContainer key={field.key}>
        <p>{field.label}</p>
        {renderedValue}
      </FieldContainer>
    );
  };

  return (
    <div>
      <TitleContainer>
        <Title>{title}</Title>
        {isAdmin && (
          <Button text onClick={toggleModal}>
            Edit
          </Button>
        )}
      </TitleContainer>
      <FieldsContainer>
        <FieldsColumn>
          {fields.slice(0, 4).map((field) => displayField(field))}{" "}
        </FieldsColumn>
        {isDesktop && <Divider />}
        <FieldsColumn>
          {fields.slice(4).map((field) => displayField(field))}
        </FieldsColumn>
      </FieldsContainer>
      {modalIsOpen && (
        <EditFieldsModal
          isOpen={modalIsOpen}
          onClose={toggleModal}
          title={title}
          fields={fields}
        />
      )}
    </div>
  );
};

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 5px;

  button {
    width: auto;
  }
`;

const Title = styled(Body1)`
  font-weight: 500;
  color: var(--header-text-color);
`;

const FieldsContainer = styled.div`
  display: flex;

  ${Body2} {
    color: #999999;
  }

  ${({ theme }) => theme.media.mobile} {
    flex-direction: column;
    gap: 15px;
  }
`;

const FieldsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 250px;

  ${({ theme }) => theme.media.tablet} {
    width: 200px;
  }

  ${({ theme }) => theme.media.mobile} {
    width: 100%;
  }
`;

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

export const Divider = styled.div`
  width: 1px;
  background: var(--border-color);
  margin-right: 70px;

  ${({ theme }) => theme.media.tablet} {
    margin-right: 50px;
  }
`;
